import { useState } from 'react';
import { AlertBanner, Badge, Button, Dialog, EmptyState, Input, Select, Toast } from '@assist/ui';
import { renderProblem } from '@assist/i18n';
import {
  ApiError,
  endpoints,
  type ConsultantAssignmentRow,
  type IncidentRow,
  type JobRunRow,
  type TenantRow,
} from '../state/api.js';
import { useSession } from '../state/session.js';
import { useResource } from '../lib/useResource.js';
import { formatDate, formatDateTime, formatMonth } from '../lib/format.js';

/**
 * GELOMBANG M15 — KENDALI UNTUK LIMA BELAS RUTE YANG SEBELUMNYA MENJAWAB `501`.
 *
 * KENAPA BERKAS TERSENDIRI, DAN BUKAN TAMBAHAN DI `Platform.tsx`
 * -------------------------------------------------------------
 * `Platform.tsx` sudah 1.642 baris dan memuat TUJUH layar. Menambahkan sepuluh kartu lagi
 * ke dalamnya membuat berkas yang tidak dapat dibaca satu duduk — dan pembaca berikutnya
 * yang tidak dapat membaca sebuah berkas akan menyalin pola dari bagian yang salah.
 *
 * Kedua berkas TERDAFTAR BERSAMA pada baris `berkas` di `docs/kontrak-elemen-figma.json`,
 * jadi gerbang elemen membandingkan kontrak dengan keduanya. Sebuah penanda `data-figma`
 * yang pindah antara keduanya tidak memerahkan apa pun, dan itu memang yang diinginkan:
 * yang dikontrak adalah RANCANGANNYA, bukan tempat tinggal kodenya.
 *
 * ACUAN FIGMA
 * -----------
 *   `223:2350` / `223:2420`  Tenant Lifecycle & Access  (tambahan pada Tenant Detail 67:56)
 *   `225:2370` / `225:2433`  Jobs & Incidents           (tambahan pada System Health 43:425)
 *   `226:2402` / `226:2451`  Audit Anchors & Export     (tambahan pada Audit Trail 43:2)
 *
 * Ketiganya DIRANCANG LEBIH DULU, sebelum satu baris JSX di berkas ini ditulis, memakai
 * component set yang sudah ada — Button `13:15`, Badge `107:17`, Input `14:11`, Dropdown
 * `164:11` — dengan nol nilai heksadesimal langsung.
 */

/** Nada lencana per kegawatan insiden. Satu tempat, dipakai di dua daftar. */
function toneInsiden(severity: string): 'danger' | 'warning' | 'information' {
  if (severity === 'SEV1') return 'danger';
  if (severity === 'SEV2') return 'warning';
  return 'information';
}

/** Nada lencana per keadaan job. `QUEUED` netral: menunggu bukan kegagalan. */
function toneJob(state: string): 'danger' | 'success' | 'neutral' {
  if (state === 'FAILED') return 'danger';
  if (state === 'SUCCEEDED') return 'success';
  return 'neutral';
}

/**
 * KARTU SIKLUS HIDUP DAN PAKET · Figma `223:2353` dan `223:2369`.
 *
 * DUA KENDALI YANG AKIBATNYA HARUS DIBACA SEBELUM DITEKAN, dan keduanya punya dialog:
 * menangguhkan mengeluarkan SETIAP orang di organisasi itu seketika, dan mengubah paket
 * adalah perbuatan komersial yang tercatat. Kalimat akibatnya ada di dialognya, bukan di
 * bawah tombolnya — kalimat yang berdiri sesudah tombol dibaca sesudah tombolnya ditekan.
 */
export function KartuSiklusHidup({
  tenant,
  plans,
  onChanged,
}: {
  tenant: TenantRow;
  plans: Array<{ code: string; name: string }>;
  onChanged: () => void | Promise<void>;
}) {
  const { t, locale, translator } = useSession();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [minta, setMinta] = useState<'SUSPEND' | 'RESUME' | 'PLAN' | null>(null);
  const [alasan, setAlasan] = useState('');
  const [paket, setPaket] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  async function jalankan() {
    setBusy(true);
    setProblem(null);
    try {
      if (minta === 'SUSPEND') {
        await endpoints.suspendTenant(tenant.id, alasan.trim() === '' ? {} : { reason: alasan.trim() });
      } else if (minta === 'RESUME') {
        await endpoints.resumeTenant(tenant.id, alasan.trim() === '' ? {} : { reason: alasan.trim() });
      } else if (minta === 'PLAN') {
        await endpoints.setTenantPlan(tenant.id, {
          plan_code: paket,
          ...(alasan.trim() === '' ? {} : { reason: alasan.trim() }),
        });
      }
      setMinta(null);
      setAlasan('');
      await onChanged();
      setToast(t('app.save'));
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  const aktif = tenant.state === 'ACTIVE';
  const tertangguh = tenant.state === 'SUSPENDED';
  const namaPaket = plans.find((p) => p.code === paket)?.name ?? paket;

  return (
    <>
      <section className="card" data-figma="platform.tenant.siklusHidup">
        <h2 className="card__title">{t('platform.lifecycle.title')}</h2>
        <p className="card__muted">{t('platform.lifecycle.body')}</p>
        <dl className="facts">
          <div>
            <dt>{t('platform.column.state')}</dt>
            <dd>
              <Badge badgeTone={aktif ? 'SUCCESS' : tertangguh ? 'WARNING' : 'NEUTRAL'}>
                {t(`platform.tenantState.${tenant.state}`)}
              </Badge>
            </dd>
          </div>
        </dl>
        {/*
          TOMBOLNYA HANYA SATU PADA SETIAP KEADAAN, dan yang tidak berlaku TIDAK ADA —
          bukan dimatikan. Sebuah tombol mati mengajak orang mencobanya, lalu menjelaskan
          dirinya lewat penolakan; pola yang sama sudah dipakai modul inti di layar ini.
        */}
        {aktif ? (
          <Button
            variant="destructive"
            data-figma="platform.tenant.tangguhkan"
            onClick={() => {
              setAlasan('');
              setProblem(null);
              setMinta('SUSPEND');
            }}
          >
            {t('platform.lifecycle.suspend')}
          </Button>
        ) : null}
        {tertangguh ? (
          <Button
            variant="secondary"
            data-figma="platform.tenant.pulihkan"
            onClick={() => {
              setAlasan('');
              setProblem(null);
              setMinta('RESUME');
            }}
          >
            {t('platform.lifecycle.resume')}
          </Button>
        ) : null}
      </section>

      <section className="card" data-figma="platform.tenant.paket">
        <h2 className="card__title">{t('platform.plan.title')}</h2>
        <p className="card__muted">{t('platform.plan.body')}</p>
        <dl className="facts">
          <div>
            <dt>{t('platform.plan.current')}</dt>
            <dd>{plans.find((p) => p.code === tenant.plan_code)?.name ?? tenant.plan_code ?? '—'}</dd>
          </div>
        </dl>
        <Select
          label={t('platform.plan.choose')}
          options={plans
            .filter((p) => p.code !== tenant.plan_code)
            .map((p) => ({ value: p.code, label: p.name }))}
          placeholder={t('platform.newTenant.plan.choose')}
          value={paket}
          onChange={(e) => setPaket(e.target.value)}
        />
        <Button
          variant="primary"
          data-figma="platform.tenant.ubahPaket"
          disabled={paket === ''}
          onClick={() => {
            setAlasan('');
            setProblem(null);
            setMinta('PLAN');
          }}
        >
          {t('platform.plan.save')}
        </Button>
      </section>

      <Dialog
        open={minta !== null}
        kind={minta === 'SUSPEND' ? 'destructive' : 'confirm'}
        title={
          minta === 'SUSPEND'
            ? t('platform.lifecycle.suspend.title', { name: tenant.name })
            : minta === 'RESUME'
              ? t('platform.lifecycle.resume.title', { name: tenant.name })
              : t('platform.plan.title.confirm', { name: tenant.name, plan: namaPaket })
        }
        description={
          minta === 'SUSPEND'
            ? t('platform.lifecycle.suspend.body')
            : minta === 'RESUME'
              ? t('platform.lifecycle.resume.body')
              : t('platform.plan.body.confirm')
        }
        confirmLabel={t('app.save')}
        cancelLabel={t('app.cancel')}
        busy={busy}
        onConfirm={() => void jalankan()}
        onCancel={() => setMinta(null)}
      >
        {problem ? (
          <AlertBanner
            kind="danger"
            signalWord={t('signal.danger')}
            title={renderProblem(translator, problem.problem)}
          />
        ) : null}
        <Input
          label={t('platform.lifecycle.reason')}
          value={alasan}
          onChange={(e) => setAlasan(e.target.value)}
        />
      </Dialog>

      {toast ? (
        <div className="assist-toast-stack">
          <Toast
            kind="success"
            signalWord={t('signal.success')}
            title={toast}
            onDismiss={() => setToast(null)}
            closeLabel={t('app.dismiss')}
          />
        </div>
      ) : null}
      {/* `locale` dipakai kartu-kartu di bawah; disebut di sini supaya lint tidak
          menganggapnya tak terpakai ketika kartunya dipindahkan. */}
      <span hidden>{locale}</span>
    </>
  );
}

/** KARTU PEMAKAIAN · Figma `223:2386`. */
export function KartuPemakaian({ tenantId }: { tenantId: string }) {
  const { t, locale } = useSession();
  const data = useResource(async () => endpoints.tenantUsage(tenantId), [tenantId]);

  return (
    <section className="card" data-figma="platform.tenant.pemakaian">
      <h2 className="card__title">{t('platform.usage.title')}</h2>
      {data.data ? (
        <p className="card__muted">
          {t('platform.usage.body', { period: formatMonth(data.data.period_month, locale) })}
        </p>
      ) : null}
      {data.data && data.data.meters.length === 0 ? (
        <EmptyState title={t('platform.usage.empty.title')} body={t('platform.usage.empty.body')} />
      ) : null}
      {data.data && data.data.meters.length > 0 ? (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">{t('platform.usage.column.meter')}</th>
              <th scope="col">{t('platform.usage.column.value')}</th>
              <th scope="col">{t('platform.usage.column.quota')}</th>
            </tr>
          </thead>
          <tbody>
            {data.data.meters.map((m) => (
              <tr key={`${m.module_code}:${m.metered_by}`}>
                <td>
                  {m.module_code} · {m.metered_by}
                </td>
                {/*
                  NULL BUKAN NOL. "Belum diukur" dan "nol" adalah dua jawaban yang
                  berbeda, dan menampilkan nol untuk yang pertama membuat admin platform
                  mengira organisasi dengan 148 pengguna aktif tidak memakai apa pun —
                  kelas cacat UAT U2-12b, dari sisi layar.
                */}
                <td>{m.value === null ? t('platform.usage.unknown') : String(m.value)}</td>
                <td>{m.quota_value === null ? '—' : String(m.quota_value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}

/** KARTU LOG PENYEDIAAN · Figma `223:2396`. */
export function KartuLogPenyediaan({ tenantId }: { tenantId: string }) {
  const { t, locale } = useSession();
  const data = useResource(
    async () => (await endpoints.tenantProvisioningLog(tenantId)).items,
    [tenantId],
  );

  return (
    <section className="card" data-figma="platform.tenant.logPenyediaan">
      <h2 className="card__title">{t('platform.provlog.title')}</h2>
      <p className="card__muted">{t('platform.provlog.body')}</p>
      {data.data && data.data.length === 0 ? (
        <EmptyState
          title={t('platform.provlog.empty.title')}
          body={t('platform.provlog.empty.body')}
        />
      ) : null}
      {data.data && data.data.length > 0 ? (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">{t('platform.audit.column.when')}</th>
              <th scope="col">{t('platform.audit.column.event')}</th>
              <th scope="col">{t('platform.audit.column.module')}</th>
            </tr>
          </thead>
          <tbody>
            {data.data.map((row) => (
              <tr key={row.id}>
                <td>{formatDateTime(row.occurred_at, locale)}</td>
                <td>{row.event_code}</td>
                <td>{row.module_code ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}

/**
 * KARTU AKSES KONSULTAN · Figma `223:2409`.
 *
 * SATU-SATUNYA PENGECUALIAN ISOLASI YANG DINYATAKAN, dan kartunya mengatakannya. Daftar
 * konsultan yang dapat ditugaskan diambil dari `GET /v1/platform/memberships` — daftar
 * pegawai internal PQM, yang admin platform memang berhak melihatnya — dan disaring ke
 * `role_code === 'KON'` yang masih aktif. Tidak ada kotak isian bebas untuk ULID:
 * mengetik pengenal orang dengan tangan adalah cara paling mudah memberi akses kepada
 * orang yang salah.
 */
export function KartuAksesKonsultan({ tenant }: { tenant: TenantRow }) {
  const { t, locale, translator } = useSession();
  const data = useResource(async () => {
    const [assignments, members] = await Promise.all([
      endpoints.consultantAssignments(tenant.id),
      endpoints.platformMemberships(),
    ]);
    return {
      items: assignments.items,
      consultants: members.items.filter(
        (m) => m.role_code === 'KON' && m.state === 'ACTIVE' && m.revoked_at === null,
      ),
    };
  }, [tenant.id]);

  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [menugaskan, setMenugaskan] = useState(false);
  const [mencabut, setMencabut] = useState<ConsultantAssignmentRow | null>(null);
  const [pilihan, setPilihan] = useState('');

  async function tugaskan() {
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.assignConsultant({ consultant_user_id: pilihan, tenant_id: tenant.id });
      setMenugaskan(false);
      setPilihan('');
      await data.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  async function cabut() {
    if (!mencabut) return;
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.revokeConsultant(mencabut.consultant_user_id, mencabut.tenant_id);
      setMencabut(null);
      await data.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  const loaded = data.data;
  return (
    <section className="card" data-figma="platform.tenant.aksesKonsultan">
      <h2 className="card__title">{t('platform.consultants.title')}</h2>
      <p className="card__muted">{t('platform.consultants.body')}</p>

      {loaded && loaded.items.length === 0 ? (
        <EmptyState
          title={t('platform.consultants.empty.title')}
          body={t('platform.consultants.empty.body')}
        />
      ) : null}

      {loaded && loaded.items.length > 0 ? (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">{t('platform.consultants.choose')}</th>
              <th scope="col">{t('platform.audit.column.when')}</th>
              <th scope="col">{t('locations.column.action')}</th>
            </tr>
          </thead>
          <tbody>
            {loaded.items.map((row) => (
              <tr key={`${row.consultant_user_id}:${row.tenant_id}`}>
                <td>
                  {row.consultant_name ?? row.consultant_email ?? row.consultant_user_id}
                  <br />
                  <span className="card__muted">{row.consultant_email ?? ''}</span>
                </td>
                <td>
                  {t('platform.consultants.granted', {
                    at: formatDate(row.granted_at, locale),
                    by: row.granted_by_name ?? '—',
                  })}
                </td>
                <td>
                  <Button
                    variant="text"
                    data-figma="platform.tenant.cabutKonsultan"
                    onClick={() => {
                      setProblem(null);
                      setMencabut(row);
                    }}
                  >
                    {t('platform.consultants.revoke')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      <Button
        variant="primary"
        data-figma="platform.tenant.tugaskanKonsultan"
        onClick={() => {
          setProblem(null);
          setPilihan('');
          setMenugaskan(true);
        }}
      >
        {t('platform.consultants.assign')}
      </Button>

      <Dialog
        open={menugaskan}
        kind="confirm"
        title={t('platform.consultants.assign.title', { name: tenant.name })}
        description={t('platform.consultants.assign.body')}
        confirmLabel={t('app.save')}
        cancelLabel={t('app.cancel')}
        busy={busy}
        confirmDisabled={pilihan === ''}
        onConfirm={() => void tugaskan()}
        onCancel={() => setMenugaskan(false)}
      >
        {problem ? (
          <AlertBanner
            kind="danger"
            signalWord={t('signal.danger')}
            title={renderProblem(translator, problem.problem)}
          />
        ) : null}
        <Select
          label={t('platform.consultants.choose')}
          options={(loaded?.consultants ?? []).map((c) => ({
            value: c.user_id,
            label: `${c.full_name ?? c.email} · ${c.email}`,
          }))}
          placeholder={t('platform.consultants.choose.placeholder')}
          value={pilihan}
          onChange={(e) => setPilihan(e.target.value)}
        />
      </Dialog>

      <Dialog
        open={mencabut !== null}
        kind="destructive"
        title={t('platform.consultants.revoke.title', {
          name: mencabut?.consultant_name ?? mencabut?.consultant_email ?? '',
        })}
        description={t('platform.consultants.revoke.body')}
        confirmLabel={t('platform.consultants.revoke')}
        cancelLabel={t('app.cancel')}
        busy={busy}
        onConfirm={() => void cabut()}
        onCancel={() => setMencabut(null)}
      >
        {problem ? (
          <AlertBanner
            kind="danger"
            signalWord={t('signal.danger')}
            title={renderProblem(translator, problem.problem)}
          />
        ) : null}
      </Dialog>
    </section>
  );
}

/**
 * PANEL PEKERJAAN LATAR DAN INSIDEN · Figma `225:2370` / `225:2433`.
 *
 * SATU PEMUATAN, DUA PERMINTAAN — bukan satu per baris. Daftar job dan daftar insiden
 * diambil bersamaan; tidak ada permintaan tambahan per baris, dan tidak ada penyegaran
 * berkala yang membuat layar ini memanggil API setiap beberapa detik.
 */
export function PanelPekerjaanDanInsiden() {
  const { t, locale, translator } = useSession();
  const data = useResource(async () => {
    const [jobs, incidents] = await Promise.all([
      endpoints.platformJobs(),
      endpoints.platformIncidents(),
    ]);
    return { jobs: jobs.items, incidents: incidents.items };
  }, []);

  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [membuka, setMembuka] = useState(false);
  const [menutup, setMenutup] = useState<IncidentRow | null>(null);
  const [severity, setSeverity] = useState<'SEV1' | 'SEV2' | 'SEV3'>('SEV2');
  const [judul, setJudul] = useState('');
  const [keterangan, setKeterangan] = useState('');
  const [kodeJob, setKodeJob] = useState('');

  async function retry(job: JobRunRow) {
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.retryJob(job.id);
      await data.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  async function buka() {
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.openIncident({
        severity,
        title: judul.trim(),
        ...(keterangan.trim() === '' ? {} : { detail: keterangan.trim() }),
        ...(kodeJob.trim() === '' ? {} : { related_job_code: kodeJob.trim() }),
      });
      setMembuka(false);
      setJudul('');
      setKeterangan('');
      setKodeJob('');
      await data.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  async function tutup() {
    if (!menutup) return;
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.patchIncident(menutup.id, { action: 'CLOSE' });
      setMenutup(null);
      await data.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  const loaded = data.data;
  return (
    <>
      {problem ? (
        <AlertBanner
          kind="danger"
          signalWord={t('signal.danger')}
          title={renderProblem(translator, problem.problem)}
        />
      ) : null}

      <section className="card" data-figma="platform.health.pekerjaanLatar">
        <h2 className="card__title">{t('platform.jobs.title')}</h2>
        <p className="card__muted">{t('platform.jobs.body')}</p>
        {loaded && loaded.jobs.length === 0 ? (
          <EmptyState title={t('platform.jobs.empty.title')} body={t('platform.jobs.empty.body')} />
        ) : null}
        {loaded && loaded.jobs.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('platform.jobs.column.job')}</th>
                <th scope="col">{t('platform.jobs.column.when')}</th>
                <th scope="col">{t('platform.jobs.column.state')}</th>
                <th scope="col">{t('locations.column.action')}</th>
              </tr>
            </thead>
            <tbody>
              {loaded.jobs.map((job) => (
                <tr key={job.id}>
                  <td>
                    {job.job_code}
                    <br />
                    <span className="card__muted">
                      {job.module_code} · {t('platform.jobs.attempt', { n: job.attempts })}
                      {job.retry_of ? ` · ${t('platform.jobs.retryOf')}` : ''}
                    </span>
                  </td>
                  <td>{formatDateTime(job.scheduled_at, locale)}</td>
                  <td>
                    <Badge tone={toneJob(job.state)}>{t(`platform.jobState.${job.state}`)}</Badge>
                    {job.error_code ? (
                      <>
                        <br />
                        <span className="card__muted">{job.error_code}</span>
                      </>
                    ) : null}
                  </td>
                  <td>
                    {/*
                      TOMBOL HANYA PADA JOB YANG BENAR-BENAR DAPAT DIJALANKAN ULANG.
                      Job yang manifest umumkan tanpa pekerja di belakangnya memperoleh
                      KALIMAT, bukan tombol mati: tombol mati mengajak orang mencobanya,
                      dan kalimat ini mengatakan sebabnya tanpa harus dicoba.
                    */}
                    {job.state === 'FAILED' ? (
                      <Button
                        variant="secondary"
                        busy={busy}
                        data-figma="platform.health.jalankanUlang"
                        onClick={() => void retry(job)}
                      >
                        {t('platform.jobs.retry')}
                      </Button>
                    ) : (
                      <span className="card__muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </section>

      <section className="card" data-figma="platform.health.insiden">
        <h2 className="card__title">{t('platform.incidents.title')}</h2>
        <p className="card__muted">{t('platform.incidents.body')}</p>
        {loaded && loaded.incidents.length === 0 ? (
          <EmptyState
            title={t('platform.incidents.empty.title')}
            body={t('platform.incidents.empty.body')}
            action={{ label: t('platform.incidents.open'), onClick: () => setMembuka(true) }}
          />
        ) : null}
        {loaded && loaded.incidents.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('platform.incidents.headline')}</th>
                <th scope="col">{t('platform.incidents.severity')}</th>
                <th scope="col">{t('platform.column.state')}</th>
                <th scope="col">{t('locations.column.action')}</th>
              </tr>
            </thead>
            <tbody>
              {loaded.incidents.map((inc) => (
                <tr key={inc.id}>
                  <td>
                    {inc.title}
                    <br />
                    <span className="card__muted">
                      {inc.state === 'OPEN'
                        ? t('platform.incidents.openedAt', {
                            at: formatDateTime(inc.opened_at, locale),
                          })
                        : t('platform.incidents.closedAt', {
                            at: formatDateTime(String(inc.closed_at), locale),
                          })}
                    </span>
                  </td>
                  <td>
                    <Badge tone={toneInsiden(inc.severity)}>{inc.severity}</Badge>
                  </td>
                  <td>
                    <Badge tone={inc.state === 'OPEN' ? 'warning' : 'neutral'}>
                      {t(`platform.incidentState.${inc.state}`)}
                    </Badge>
                  </td>
                  <td>
                    {inc.state === 'OPEN' ? (
                      <Button
                        variant="secondary"
                        busy={busy}
                        data-figma="platform.health.tutupInsiden"
                        onClick={() => setMenutup(inc)}
                      >
                        {t('platform.incidents.close')}
                      </Button>
                    ) : (
                      <span className="card__muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        <Button
          variant="primary"
          data-figma="platform.health.bukaInsiden"
          onClick={() => {
            setProblem(null);
            setMembuka(true);
          }}
        >
          {t('platform.incidents.open')}
        </Button>
      </section>

      <Dialog
        open={membuka}
        kind="confirm"
        title={t('platform.incidents.open.title')}
        description={t('platform.incidents.open.body')}
        confirmLabel={t('app.save')}
        cancelLabel={t('app.cancel')}
        busy={busy}
        confirmDisabled={judul.trim().length < 3}
        onConfirm={() => void buka()}
        onCancel={() => setMembuka(false)}
      >
        <Select
          label={t('platform.incidents.severity')}
          options={[
            { value: 'SEV1', label: 'SEV1' },
            { value: 'SEV2', label: 'SEV2' },
            { value: 'SEV3', label: 'SEV3' },
          ]}
          value={severity}
          onChange={(e) => setSeverity(e.target.value as 'SEV1' | 'SEV2' | 'SEV3')}
        />
        <Input
          label={t('platform.incidents.headline')}
          value={judul}
          required
          onChange={(e) => setJudul(e.target.value)}
        />
        <Input
          label={t('platform.incidents.detail')}
          value={keterangan}
          onChange={(e) => setKeterangan(e.target.value)}
        />
        <Input
          label={t('platform.incidents.relatedJob')}
          value={kodeJob}
          onChange={(e) => setKodeJob(e.target.value)}
        />
      </Dialog>

      <Dialog
        open={menutup !== null}
        kind="confirm"
        title={t('platform.incidents.close.title', { title: menutup?.title ?? '' })}
        description={t('platform.incidents.close.body')}
        confirmLabel={t('platform.incidents.close')}
        cancelLabel={t('app.cancel')}
        busy={busy}
        onConfirm={() => void tutup()}
        onCancel={() => setMenutup(null)}
      />
    </>
  );
}

/**
 * PANEL JANGKAR DAN EKSPOR JEJAK · Figma `226:2402` / `226:2451`.
 *
 * KARTU JANGKAR ADA UNTUK MEMBUKTIKAN SEBUAH KALIMAT YANG SUDAH TERTULIS DI LAYAR INI:
 * jejak "tidak dapat diubah dan tidak dapat dihapus — termasuk oleh pemegang basis
 * datanya". Verifikasi rantai berjalan DARI jangkar terakhir, dan tanpa jangkar ia hanya
 * memeriksa rantai terhadap dirinya sendiri. Keadaan "belum ada jangkar" karena itu
 * DIKATAKAN, bukan disembunyikan di balik daftar kosong.
 */
export function PanelJangkarDanEkspor() {
  const { t, locale, translator } = useSession();
  const data = useResource(async () => {
    const [anchors, integrity] = await Promise.all([
      endpoints.auditAnchors(),
      endpoints.auditIntegrity(),
    ]);
    return { anchors: anchors.items, integrity };
  }, []);

  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [dari, setDari] = useState('');
  const [sampai, setSampai] = useState('');
  const [hasil, setHasil] = useState<{ rows: number; filename: string } | null>(null);

  async function ekspor() {
    setBusy(true);
    setProblem(null);
    setHasil(null);
    try {
      const out = await endpoints.exportAudit({
        ...(dari === '' ? {} : { from: new Date(`${dari}T00:00:00Z`).toISOString() }),
        ...(sampai === '' ? {} : { to: new Date(`${sampai}T00:00:00Z`).toISOString() }),
      });
      /**
       * Berkasnya sampai di dalam jawabannya, jadi unduhannya terjadi di peramban —
       * tanpa URL kedua, tanpa penjajakan, dan tanpa objek yang tertinggal di
       * penyimpanan untuk dibersihkan seseorang kelak.
       */
      const biner = Uint8Array.from(atob(out.content_base64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([biner], { type: out.media_type }));
      const a = document.createElement('a');
      a.href = url;
      a.download = out.filename;
      a.click();
      URL.revokeObjectURL(url);
      setHasil({ rows: out.row_count, filename: out.filename });
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  const loaded = data.data;
  return (
    <>
      <section className="card" data-figma="platform.audit.jangkar">
        <h2 className="card__title">{t('platform.anchors.title')}</h2>
        <p className="card__muted">{t('platform.anchors.body')}</p>
        {loaded ? (
          <AlertBanner
            kind={loaded.integrity.ok ? 'success' : 'danger'}
            signalWord={t(loaded.integrity.ok ? 'signal.success' : 'signal.danger')}
            title={
              loaded.integrity.ok
                ? t('platform.anchors.verified', { checked: loaded.integrity.checked })
                : t('platform.anchors.broken', { seq: loaded.integrity.first_broken_seq ?? 0 })
            }
          />
        ) : null}
        {loaded && loaded.anchors.length === 0 ? (
          <EmptyState
            title={t('platform.anchors.empty.title')}
            body={t('platform.anchors.empty.body')}
          />
        ) : null}
        {loaded && loaded.anchors.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('platform.anchors.column.chain')}</th>
                <th scope="col">{t('platform.anchors.column.seq')}</th>
                <th scope="col">{t('platform.anchors.column.at')}</th>
                <th scope="col">{t('platform.anchors.column.ref')}</th>
              </tr>
            </thead>
            <tbody>
              {loaded.anchors.map((a) => (
                <tr key={`${a.chain_key}:${a.chain_seq}`}>
                  <td>{a.chain_key}</td>
                  <td>{a.chain_seq}</td>
                  <td>{formatDateTime(a.anchored_at, locale)}</td>
                  <td>{a.anchor_ref}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </section>

      <section className="card" data-figma="platform.audit.ekspor">
        <h2 className="card__title">{t('platform.export.title')}</h2>
        <p className="card__muted">{t('platform.export.body', { limit: 20000 })}</p>
        {problem ? (
          <AlertBanner
            kind="danger"
            signalWord={t('signal.danger')}
            title={renderProblem(translator, problem.problem)}
          />
        ) : null}
        {hasil ? (
          <AlertBanner
            kind="success"
            signalWord={t('signal.success')}
            title={t('platform.export.done', { rows: hasil.rows, filename: hasil.filename })}
          />
        ) : null}
        <Input
          label={t('platform.export.from')}
          inputType="date"
          value={dari}
          onChange={(e) => setDari(e.target.value)}
        />
        <Input
          label={t('platform.export.to')}
          inputType="date"
          value={sampai}
          onChange={(e) => setSampai(e.target.value)}
        />
        <Button
          variant="primary"
          busy={busy}
          data-figma="platform.audit.eksporJalankan"
          onClick={() => void ekspor()}
        >
          {t('platform.export.run')}
        </Button>
      </section>
    </>
  );
}
